import { ChevronRight, Mail, MoreHorizontal, Trash2 } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Switch } from '@/components/ui/shadcn/switch'
import { Button } from '@/components/ui/shadcn/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/shadcn/dropdown-menu'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/shadcn/alert-dialog'
import { DAY_SHORT } from './constants'
import { SourceChip } from './source-chip'
import { PlatformStack } from './platform-icon'
import { FlowChip } from './flow-rail'
import { resolveSource, sourceName, type MoodboardLike } from './source'
import type { AutomationGroup } from './grouping'
import { buildSentence, lastRunLabel, nextRunLabel } from './schedule'

interface Props {
  group: AutomationGroup
  moodboards: MoodboardLike[]
  timezone: string | undefined
  onEdit: () => void
  onDelete: () => void
  onToggleActive: (active: boolean) => void
  isNew?: boolean
}

/** Thin line + chevron that reads as a flow "→" between rail segments. */
function FlowArrow() {
  return (
    <span aria-hidden className="flex shrink-0 items-center">
      <span className="bg-border h-px w-2" />
      <ChevronRight className="text-muted-foreground h-4 w-4" />
    </span>
  )
}

/** Most recent lastTriggeredAt across the group's rows, for the meta line. */
function latestTriggeredAt(group: AutomationGroup): string | null {
  let latest: number | null = null
  let raw: string | null = null
  for (const row of group.rows) {
    if (!row.lastTriggeredAt) continue
    const ms = new Date(row.lastTriggeredAt).getTime()
    if (latest === null || ms > latest) {
      latest = ms
      raw = row.lastTriggeredAt as unknown as string
    }
  }
  return raw
}

export function AutomationCard({
  group,
  moodboards,
  timezone,
  onEdit,
  onDelete,
  onToggleActive,
  isNew,
}: Props) {
  const source = resolveSource(group.representative, moodboards)
  const sentence = buildSentence(group.days, { name: sourceName(source) }, group.platforms)
  const active = group.sendReminders
  const isEveryDay = group.days.length === 7
  // Day pills carry the active/paused signal (green vs red) — solid tints that
  // stay legible in both themes, mirroring the amber status-pill pattern.
  const pillClass = cn(
    'rounded-md px-1.5 py-0.5 text-[11px] font-semibold',
    active
      ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300'
      : 'bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-300',
  )

  return (
    <div
      className={cn(
        'bg-card group rounded-xl p-4 transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md',
        isNew && 'animate-fade-up animate-duration-500',
      )}>
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={onEdit}
          aria-label={`Edit: ${sentence}`}
          className="flex min-w-0 flex-1 flex-col items-start gap-1.5 text-left">
          <SourceChip source={source} size="sm" className="min-w-0" />
          {/* Compact left-to-right flow, always on its own line below the source. */}
          <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <span className="flex shrink-0 items-center gap-1">
              {isEveryDay ? (
                <span className={pillClass}>Every day</span>
              ) : (
                group.days.map((d) => (
                  <span key={d} className={pillClass}>
                    {DAY_SHORT[d]}
                  </span>
                ))
              )}
              <span className="text-muted-foreground text-xs">8:00</span>
            </span>
            <FlowArrow />
            <PlatformStack platforms={group.platforms} />
            <FlowArrow />
            <FlowChip>
              <Mail className="text-muted-foreground h-3.5 w-3.5" />
              Email you
            </FlowChip>
          </span>
        </button>

        <div className="flex shrink-0 items-center gap-2">
          <span className="text-muted-foreground hidden text-xs sm:inline">
            {active ? 'Active' : 'Paused'}
          </span>
          <Switch checked={active} onCheckedChange={onToggleActive} aria-label={sentence} />

          <AlertDialog>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon-sm"
                  className="opacity-60 group-hover:opacity-100"
                  aria-label="Automation options">
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={onEdit}>Edit</DropdownMenuItem>
                <AlertDialogTrigger asChild>
                  <DropdownMenuItem
                    onSelect={(e) => e.preventDefault()}
                    className="text-destructive">
                    <Trash2 />
                    Delete
                  </DropdownMenuItem>
                </AlertDialogTrigger>
              </DropdownMenuContent>
            </DropdownMenu>

            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Delete this automation?</AlertDialogTitle>
                <AlertDialogDescription>{sentence}</AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  onClick={onDelete}
                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
                  Delete
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>

      <div className="text-muted-foreground mt-2 flex items-center gap-2 text-xs">
        {/* Day pills already signal paused (red), so no amber pill here — just
            last-run, plus next-run when active. */}
        <span>{lastRunLabel(latestTriggeredAt(group))}</span>
        {active && <span>· Next: {nextRunLabel(group.days, timezone)}</span>}
      </div>
    </div>
  )
}
